import React, { useState } from 'react';
import { Box, Alert, Typography } from '@mui/material';
import LoadingView from '../components/LoadingView';
import { useProfitAndLoss } from '../hooks/useProfitAndLoss';
import { useStaffHeadcount } from '../hooks/useStaffHeadcount';
import { useHeadcountPlan } from '../hooks/useHeadcountPlan';
import ProfitAndLossFilterHeader from '../components/profitAndLoss/ProfitAndLossFilterHeader';
import PerformanceSummaryCards from '../components/profitAndLoss/PerformanceSummaryCards';
import ProfitAndLossAiReviewCard from '../components/profitAndLoss/ProfitAndLossAiReviewCard';
import ProfitAndLossDetailTable from '../components/profitAndLoss/ProfitAndLossDetailTable';
import MonthlyTrendChart from '../components/profitAndLoss/MonthlyTrendChart';
import DepartmentPerformanceCards from '../components/profitAndLoss/DepartmentPerformanceCards';
import StaffHeadcountSection from '../components/profitAndLoss/StaffHeadcountSection';
import JournalLedgerDialog, { type LedgerCellContext } from '../components/trialBalance/JournalLedgerDialog';

/**
 * 経営概況詳細ページ。
 * 損益の共通フック useProfitAndLoss と表示セクションを組み合わせて構成する。
 * セクションは JSX の記述順がそのまま表示順になるため、並び替え・追加はこのファイルで完結する。
 * 従業員数セクションは経営概況ページのみで表示する。
 */
export default function ProfitAndLossStatementManagement() {
  const pl = useProfitAndLoss();
  // 補助元帳ダイアログの対象セル (null = 閉じている)。損益計算書の実績・前年実績セルクリックで開く
  const [ledgerContext, setLedgerContext] = useState<LedgerCellContext | null>(null);
  const staff = useStaffHeadcount({
    fiscalYear: pl.fiscalYear,
    branchCodes: pl.selectedBranchCodes,
    departmentCodes: pl.selectedDepartmentCodes,
    branchDeptLogic: pl.branchDeptLogic,
  });
  // 採用計画 (計画採用) を予実対比用に取得。従業員数セクションと同じ年度・スコープに合わせる
  const headcountPlan = useHeadcountPlan({
    fiscalYear: pl.fiscalYear,
    branchCodes: pl.selectedBranchCodes,
    departmentCodes: pl.selectedDepartmentCodes,
    branchDeptLogic: pl.branchDeptLogic,
  });

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100vh' }}>

      {/* ⚠️ エラー */}
      {pl.error && <Alert severity="warning" sx={{ width: '92%', mb: 2, borderRadius: '8px' }}>{pl.error}</Alert>}

      {/* ⏳ 進行状況 */}
      {pl.loading && <LoadingView message="データ更新中..." height="50vh" size={50} />}

      {/* 📭 データなき場合 */}
      {pl.rows.length === 0 && !pl.loading && !pl.error && (
        <Box sx={{ mt: 10, p: 5, border: '2px dashed #cbd5e1', borderRadius: '12px' }}>
          <Typography color="textSecondary">データが存在しません。同期を実行するか期間を確認してください。</Typography>
        </Box>
      )}

      {pl.rows.length > 0 && (
        <Box sx={{ width: '92%', mt: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ProfitAndLossFilterHeader pl={pl} />
          {/* 期間セレクタは業績サマリー側のヘッダーに集約し、損益計算書側では非表示にする */}
          <PerformanceSummaryCards
            dedupedRows={pl.dedupedRows}
            performanceRange={pl.performanceRange}
            currentMonth={pl.currentMonth}
            showRangeSelector
            setPerformanceRange={pl.setPerformanceRange}
            setCurrentMonth={pl.setCurrentMonth}
          />
          <ProfitAndLossDetailTable
            dedupedRows={pl.dedupedRows}
            fiscalYear={pl.fiscalYear}
            currentMonth={pl.currentMonth}
            setCurrentMonth={pl.setCurrentMonth}
            performanceRange={pl.performanceRange}
            setPerformanceRange={pl.setPerformanceRange}
            showRangeSelector={false}
            expandedSections={pl.expandedSections}
            toggleSection={pl.toggleSection}
            expandedAccounts={pl.expandedAccounts}
            toggleAccount={pl.toggleAccount}
            onCellClick={setLedgerContext}
          />
          <MonthlyTrendChart
            dedupedRows={pl.dedupedRows}
            fiscalYear={pl.fiscalYear}
            currentMonth={pl.currentMonth}
          />
          <DepartmentPerformanceCards
            deptPlRows={pl.deptPlRows}
            deptPlLoading={pl.deptPlLoading}
            performanceRange={pl.performanceRange}
            currentMonth={pl.currentMonth}
            fiscalYear={pl.fiscalYear}
          />
          <StaffHeadcountSection
            staff={staff}
            fiscalYear={pl.fiscalYear}
            currentMonth={pl.currentMonth}
            selectedBranchCodes={pl.selectedBranchCodes}
            plan={headcountPlan}
          />
          <ProfitAndLossAiReviewCard
            dedupedRows={pl.dedupedRows}
            deptPlRows={pl.deptPlRows}
            branchPlRows={pl.branchPlRows}
            staffRows={staff.staffRows}
            performanceRange={pl.performanceRange}
            currentMonth={pl.currentMonth}
            fiscalYear={pl.fiscalYear}
            planHiresByMonth={headcountPlan.planHiresByMonth}
            planVersionName={headcountPlan.planVersionName}
          />
        </Box>
      )}

      {/* 📒 補助元帳ドリルダウンダイアログ (損益計算書の実績・前年実績セルクリックで開く) */}
      <JournalLedgerDialog
        context={ledgerContext}
        fiscalYear={pl.fiscalYear}
        branchCodes={pl.selectedBranchCodes}
        departmentCodes={pl.selectedDepartmentCodes}
        branchDeptLogic={pl.branchDeptLogic}
        onClose={() => setLedgerContext(null)}
      />
    </Box>
  );
}
